/**
 * Управление списком архивированных чатов.
 * Хранится в localStorage. При изменении генерирует событие `chatArchiveChange`,
 * чтобы компоненты могли реагировать без перезагрузки.
 *
 * Серверный эндпоинт POST /api/chats/{chatId}/toggle-archive реализован,
 * но на случай недоступности — fallback на localStorage.
 */

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'hrnb_archivedChatIds';
const EVENT_NAME = 'chatArchiveChange';

/**
 * @returns {Set<string>} — множество ID архивированных чатов
 */
export function getArchivedChatIds() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return new Set();
        const arr = JSON.parse(raw);
        return Array.isArray(arr) ? new Set(arr) : new Set();
    } catch {
        return new Set();
    }
}

/**
 * @param {string} chatId
 * @returns {boolean}
 */
export function isChatArchived(chatId) {
    if (!chatId) return false;
    return getArchivedChatIds().has(String(chatId));
}

function persist(set) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
    } catch { /* квота / приватный режим — игнорируем */ }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { chatId: null } }));
}

/**
 * Архивировать чат.
 * @param {string} chatId
 */
export function archiveChat(chatId) {
    if (!chatId) return;
    const set = getArchivedChatIds();
    set.add(String(chatId));
    persist(set);
}

/**
 * Разархивировать чат.
 * @param {string} chatId
 */
export function unarchiveChat(chatId) {
    if (!chatId) return;
    const set = getArchivedChatIds();
    set.delete(String(chatId));
    persist(set);
}

/**
 * Переключить архивный статус чата (только localStorage).
 * @param {string} chatId
 * @returns {boolean} — новое состояние (true = архивирован)
 */
export function toggleChatArchiveLocal(chatId) {
    if (!chatId) return false;
    if (isChatArchived(chatId)) {
        unarchiveChat(chatId);
        return false;
    }
    archiveChat(chatId);
    return true;
}

/**
 * Применить архивный статус к списку чатов — добавляет isArchived каждому чату.
 * Если чат уже имеет isArchived с сервера — приоритет за сервером.
 * @param {Array} chats - Список чатов
 * @returns {Array} Тот же список с добавленным полем isArchived
 */
export function applyArchivedState(chats) {
    const archivedIds = getArchivedChatIds();
    return chats.map(chat => ({
        ...chat,
        isArchived: chat.isArchived ?? archivedIds.has(String(chat.id))
    }));
}

/**
 * React-хук: подписка на изменения списка архивированных чатов.
 * Возвращает текущий Set ID.
 *
 * @returns {Set<string>}
 */
export function useArchivedChatIds() {
    const [archived, setArchived] = useState(() => getArchivedChatIds());

    useEffect(() => {
        const handler = () => setArchived(getArchivedChatIds());
        window.addEventListener(EVENT_NAME, handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener(EVENT_NAME, handler);
            window.removeEventListener('storage', handler);
        };
    }, []);

    return archived;
}
